import { InjectionToken } from '@angular/core';

import type { AuthAudience } from './models';

/**
 * Per-app auth wiring. The CRM internal app provides one set
 * (`/api/v1/auth/*`, audience='internal'), the customer portal provides
 * another (`/api/v1/portal/auth/*`, audience='customer'). Lets one
 * shared-auth lib serve both surfaces.
 *
 * Paths are *full* paths (including the API base prefix), because
 * shared-auth talks to `HttpClient` directly rather than going through
 * `ApiClientService` — the latter unwraps the `{ data }` envelope and
 * also stamps `Idempotency-Key`, both of which we want to manage
 * ourselves for auth.
 */
export interface EbtAuthConfig {
  audience: AuthAudience;
  /** POST — body { email, password }. */
  loginPath: string;
  /** POST — relies on the refresh cookie set by login. */
  refreshPath: string;
  /** POST — clears the server session + refresh cookie. */
  logoutPath: string;
  /** GET — returns the current `AuthedUser`. */
  mePath: string;
  /** Route the app navigates to when auth is required but absent (e.g. `/login`). */
  loginRoute: string;
  /** Default landing route after successful login (e.g. `/`). */
  postLoginRoute: string;
  /**
   * POST — body { email }. Sends a password-reset email. Always resolves
   * 2xx (no account-existence leak). Optional: apps that don't offer
   * self-serve reset simply omit it.
   */
  passwordResetRequestPath?: string;
  /** POST — body { token, newPassword }. Consumes the emailed token. */
  passwordResetConfirmPath?: string;
}

export const AUTH_CONFIG = new InjectionToken<EbtAuthConfig>('EBT_AUTH_CONFIG');
